import { Controller } from "@hotwired/stimulus"

/**
 * 可重用的 Autocomplete 控制器。
 *
 * 透過 Stimulus values 設定兩種模式：
 * - Remote 模式（有 url value）：透過 API 搜尋，附帶 debounce 和 AbortController
 * - Local 模式（有 storageKey value）：從 localStorage 讀取歷史記錄，即時過濾
 *
 * @example Remote 模式（公司搜尋）
 *   <div data-controller="autocomplete"
 *        data-autocomplete-url-value="/api/stocks/autocomplete"
 *        data-autocomplete-min-length-value="1">
 *     <input data-autocomplete-target="input" ...>
 *     <ul data-autocomplete-target="list" ...></ul>
 *   </div>
 *
 * @example Local 模式（Source 歷史）
 *   <div data-controller="autocomplete"
 *        data-autocomplete-storage-key-value="notion-source-history"
 *        data-autocomplete-min-length-value="0">
 *     <input data-autocomplete-target="input" ...>
 *     <ul data-autocomplete-target="list" ...></ul>
 *   </div>
 */
export default class extends Controller {
  static targets = ['input', 'list']

  static values = {
    url: String,            // Remote 模式的 API endpoint
    storageKey: String,     // Local 模式的 localStorage key
    minLength: { type: Number, default: 1 }  // 觸發搜尋的最少字元數
  }

  connect() {
    this.activeIndex = -1
    this.abortController = null
    this.debounceTimer = null
    this._justSelected = false

    // ARIA attributes
    this.inputTarget.setAttribute('role', 'combobox')
    this.inputTarget.setAttribute('aria-expanded', 'false')
    this.inputTarget.setAttribute('aria-autocomplete', 'list')
    this.listTarget.setAttribute('role', 'listbox')

    // 點擊外部關閉 dropdown
    this._onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) {
        this._hideList()
      }
    }
    document.addEventListener('click', this._onDocumentClick)
  }

  disconnect() {
    document.removeEventListener('click', this._onDocumentClick)
    this._cancelPendingRequest()
    clearTimeout(this.debounceTimer)
  }

  // --- Public Actions（由 Stimulus data-action 觸發）---

  onInput() {
    // 選取後觸發的 input event 不應重新開啟 dropdown
    if (this._justSelected) {
      this._justSelected = false
      return
    }

    const query = this.inputTarget.value.trim()

    if (query.length < this.minLengthValue) {
      this._hideList()
      return
    }

    if (this._isRemoteMode()) {
      this._fetchRemote(query)
    } else {
      this._filterLocal(query)
    }
  }

  onFocus() {
    const query = this.inputTarget.value.trim()

    // Local 模式 + minLength=0：focus 時顯示所有歷史
    if (this._isLocalMode() && this.minLengthValue === 0) {
      this._filterLocal(query)
      return
    }

    // 若已有輸入且長度足夠，重新顯示結果
    if (query.length >= this.minLengthValue) {
      this.onInput()
    }
  }

  onKeydown(event) {
    if (this.listTarget.classList.contains('hidden')) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this._moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this._moveActive(-1)
        break
      case 'Enter':
        event.preventDefault()
        this._selectActive()
        break
      case 'Escape':
        this._hideList()
        break
    }
  }

  // --- Private Methods ---

  _isRemoteMode() {
    return this.hasUrlValue && this.urlValue !== ''
  }

  _isLocalMode() {
    return this.hasStorageKeyValue && this.storageKeyValue !== ''
  }

  /**
   * Remote 模式：200ms debounce 後向 API 發送搜尋請求。
   * 使用 AbortController 取消前一次 in-flight 請求，避免 race condition。
   */
  _fetchRemote(query) {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(async () => {
      this._cancelPendingRequest()
      this.abortController = new AbortController()

      try {
        const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
        const response = await fetch(url, { signal: this.abortController.signal })
        const data = await response.json()

        const items = (data.results || []).map(r => ({
          label: r.label,
          value: r.label
        }))
        this._renderList(items)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Autocomplete fetch error:', error)
        }
      }
    }, 200)
  }

  /**
   * Local 模式：從 localStorage 讀取歷史記錄，case-insensitive 過濾。
   */
  _filterLocal(query) {
    const history = this._loadHistory()
    const lowerQuery = query.toLowerCase()
    const filtered = query === ''
      ? history
      : history.filter(item => item.toLowerCase().includes(lowerQuery))

    const items = filtered.map(h => ({ label: h, value: h }))
    this._renderList(items)
  }

  /**
   * 從 localStorage 讀取歷史記錄。
   *
   * @returns {string[]} 歷史記錄陣列（MRU 排序）
   */
  _loadHistory() {
    try {
      const raw = localStorage.getItem(this.storageKeyValue)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  }

  /**
   * 渲染 dropdown 列表。
   * 使用 textContent 而非 innerHTML 防止 XSS。
   */
  _renderList(items) {
    this.listTarget.innerHTML = ''
    this.activeIndex = -1

    if (items.length === 0) {
      this._hideList()
      return
    }

    items.forEach((item, index) => {
      const li = document.createElement('li')
      li.textContent = item.label
      li.setAttribute('role', 'option')
      li.setAttribute('id', `autocomplete-option-${index}`)
      li.className = 'px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 transition-colors'

      // mousedown + preventDefault 防止 blur/click race condition
      li.addEventListener('mousedown', (event) => {
        event.preventDefault()
        this._selectItem(item.value)
      })

      this.listTarget.appendChild(li)
    })

    this._showList()
  }

  _showList() {
    this.listTarget.classList.remove('hidden')
    this.inputTarget.setAttribute('aria-expanded', 'true')
  }

  _hideList() {
    this.listTarget.classList.add('hidden')
    this.inputTarget.setAttribute('aria-expanded', 'false')
    this.activeIndex = -1
    this.inputTarget.removeAttribute('aria-activedescendant')
  }

  /**
   * 鍵盤導覽：移動 active 項目的 highlight。
   */
  _moveActive(direction) {
    const items = this.listTarget.querySelectorAll('li')
    if (items.length === 0) return

    // 移除舊的 highlight
    if (this.activeIndex >= 0 && this.activeIndex < items.length) {
      items[this.activeIndex].classList.remove('bg-blue-100')
    }

    // 計算新的 index
    this.activeIndex += direction
    if (this.activeIndex < 0) this.activeIndex = items.length - 1
    if (this.activeIndex >= items.length) this.activeIndex = 0

    // 套用新的 highlight
    items[this.activeIndex].classList.add('bg-blue-100')
    items[this.activeIndex].scrollIntoView({ block: 'nearest' })
    this.inputTarget.setAttribute('aria-activedescendant', `autocomplete-option-${this.activeIndex}`)
  }

  /**
   * 選取當前 active 的項目。
   */
  _selectActive() {
    const items = this.listTarget.querySelectorAll('li')
    if (this.activeIndex >= 0 && this.activeIndex < items.length) {
      this._selectItem(items[this.activeIndex].textContent)
    }
  }

  /**
   * 將選取的值寫入 input 並關閉 dropdown。
   */
  _selectItem(value) {
    this.inputTarget.value = value
    this._hideList()
    // 標記為剛選取，避免接下來的 input event 重新開啟 dropdown
    this._justSelected = true
    // 觸發 input event 讓其他 controller 知道值已改變
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  _cancelPendingRequest() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
